// Shared forward-to-anyone dialog (Graph forward action carries the original
// attachments). Extracted from the pop-out's inline dialog.

import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { emailApi } from "@/lib/api";
import type { UnifiedEmail } from "@/lib/email/types";

interface Props {
  email: UnifiedEmail | null;
  onClose: () => void;
}

export function EmailForwardDialog({ email, onClose }: Props) {
  const { toast } = useToast();
  const [to, setTo] = useState("");
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (email) {
      setTo("");
      setComment("");
    }
  }, [email?.id]);

  const send = async () => {
    if (!email) return;
    const recipients = to.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
    if (!recipients.length) {
      toast({ title: "At least one recipient is required", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      await emailApi.forwardEmail(email.mailbox, email.id, recipients, comment.trim() || undefined);
      toast({ title: "Forwarded", description: `Sent to ${recipients.join(", ")}` });
      onClose();
    } catch (err: any) {
      toast({ title: "Could not forward", description: err.message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!email} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="w-[480px] max-w-[calc(100vw-2rem)]">
        <DialogHeader>
          <DialogTitle>Forward email</DialogTitle>
          <DialogDescription className="text-xs">
            Forwards from {email?.mailbox} with the original attachments.
          </DialogDescription>
        </DialogHeader>
        {email && (
          <p className="truncate rounded-md bg-muted/50 p-2 text-sm font-medium">
            {email.subject || "(no subject)"}
          </p>
        )}
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-sm font-medium">To</label>
            <Input
              placeholder="one@example.com, two@example.com"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-muted-foreground">
              Comment <span className="text-xs font-normal">(optional)</span>
            </label>
            <Textarea
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="resize-none text-sm"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={sending} onClick={send}>
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Forward"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
